import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { ApiKeysProvider } from "./contexts/ApiKeysContext";
import { trackGoogleAdsPageView, trackGoogleAdsConversion, CONVERSION_LABELS } from "./utils/googleAdsTracking";

// Eager loading for public/auth pages
import LandingPage from "./pages/LandingPage";
import Pricing from "./pages/Pricing";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import TermsOfService from "./pages/TermsOfService";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import PaymentSuccess from "./pages/PaymentSuccess";
import CompletePayment from "./pages/CompletePayment";

// Lazy loading for protected app pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ChatWindow = lazy(() => import("./pages/ChatWindow"));
const MLSGPT = lazy(() => import("./pages/MLSGPT"));
const MLSGPTChat = lazy(() => import("./pages/MLSGPTChat"));
const MapResults = lazy(() => import("./pages/MapResults"));
const CMAView = lazy(() => import("./pages/CMAView"));
const MapSearch = lazy(() => import("./pages/MapSearch"));
const CMAFromScratch = lazy(() => import("./pages/CMAFromScratch"));
const VirtualStaging = lazy(() => import("./pages/VirtualStaging"));
const VideoMontage = lazy(() => import("./pages/VideoMontage"));
const TaxRecordSearch = lazy(() => import("./pages/TaxRecordSearch"));
const CoordinateSearch = lazy(() => import("./pages/CoordinateSearch"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const TeamLicenseManagement = lazy(() => import("./pages/TeamLicenseManagement"));
const BrokerDashboard = lazy(() => import("./pages/BrokerDashboard"));

import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";

// Component to track route changes
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/contact') {
      trackGoogleAdsPageView(path + location.search);

      if (path === '/' && CONVERSION_LABELS.HOME_PAGE_VIEW) {
        trackGoogleAdsConversion(CONVERSION_LABELS.HOME_PAGE_VIEW);
      }
    }
  }, [location]);

  return null;
}

function AppLoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#1e3c72',
      color: 'white'
    }}>
      <div>Loading...</div>
    </div>
  );
}

function App() {
  return (
    <ApiKeysProvider>
      <Router>
        <RouteTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/complete-payment" element={<CompletePayment />} />

          {/* Protected App Routes */}
          <Route path="/app/*" element={
            <ProtectedRoute>
              <Suspense fallback={<AppLoadingFallback />}>
                <Layout>
                  <Routes>
                    <Route path="/" element={<MLSGPT />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/team-licenses" element={<TeamLicenseManagement />} />
                    <Route path="/broker" element={<BrokerDashboard />} />
                    <Route path="/chat" element={<ChatWindow />} />
                    <Route path="/mls-gpt" element={<MLSGPT />} />
                    <Route path="/cma" element={<CMAView />} />
                    <Route path="/map-search" element={<MapSearch />} />
                    <Route path="/cma-from-scratch" element={<CMAFromScratch />} />
                    <Route path="/virtual-staging" element={<VirtualStaging />} />
                    <Route path="/video-montage" element={<VideoMontage />} />
                    <Route path="/tax-record-search" element={<TaxRecordSearch />} />
                    <Route path="/coordinate-search" element={<CoordinateSearch />} />
                    <Route path="/delete-account" element={<DeleteAccountPage />} />
                  </Routes>
                </Layout>
              </Suspense>
            </ProtectedRoute>
          } />

          {/* MLS GPT Chat - No Layout/Sidebar */}
          <Route path="/app/mls-gpt-chat" element={
            <ProtectedRoute>
              <Suspense fallback={<AppLoadingFallback />}>
                <MLSGPTChat />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Map Results - Full-screen map in its own tab, No Layout/Sidebar */}
          <Route path="/app/map-results" element={
            <ProtectedRoute>
              <Suspense fallback={<AppLoadingFallback />}>
                <MapResults />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Legacy Routes - Redirect to app */}
          <Route path="/cma" element={
            <ProtectedRoute>
              <Suspense fallback={<AppLoadingFallback />}>
                <CMAView />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/map-search" element={
            <ProtectedRoute>
              <Suspense fallback={<AppLoadingFallback />}>
                <MapSearch />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/*" element={
            <ProtectedRoute>
              <Suspense fallback={<AppLoadingFallback />}>
                <MLSGPT />
              </Suspense>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </ApiKeysProvider>
  );
}

export default App;
